/**
 * «Opciones de este concepto» (OPUS ci, idea 7 — Dos opciones con precio).
 *
 * Para un renglón PRINCIPAL de la hoja: armar otra forma de construirlo («block 15» en vez de «tabique
 * rojo») con la MISMA medición y sus propios complementarios, y compararla lado a lado con lo que la hoja
 * tiene hoy (que se lee vivo de la hoja). Tres cifras (hoy · opción · diferencia), la tabla con lo que cambia
 * en ámbar, «No lleva» donde un lado no tiene el concepto y cada cantidad abre su generador.
 *
 * Una opción NO suma al total de la hoja. «Usar esta opción» la pone en la hoja y lo que había se guarda
 * como opción para volver. La lógica de comparar es pura (@supernova/shared/opcionesDelRenglon); medir,
 * proponer complementarios y guardar lo hace quien abre esto (PlanosPage, la misma que «Presupuestar todo
 * el plano»).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
  Tab,
  TabList,
} from '@fluentui/react-components';
import { Add16Regular, ArrowSwap16Regular, Delete16Regular, Print16Regular, Search16Regular } from '@fluentui/react-icons';
import {
  compararOpciones,
  claveDeConceptoOpcion,
  ladoActualDeLaHoja,
  unitsAreEquivalent,
  type FilaComparada,
  type PlanoOpcion,
  type PlanoRenglon,
  type RenglonDeOpcion,
  type SugerirConceptosResponse,
} from '@supernova/shared';
import { DocumentoGenerador } from './NumerosGeneradores.js';
import { AgregarAdicional } from './AgregarAdicional.js';
import { FieldHelp } from '../FieldHelp.js';

/** Un concepto del catálogo elegido de la lista («Buscar», gratis, o «Recomendados»). */
export interface ConceptoParaOpcion {
  resourceId: string;
  catalogId: string;
  clave: string;
  descripcion: string;
  unidad: string | null;
  precio: number | null;
  /** Por qué se recomienda (solo en «Recomendados»). */
  razon?: string;
}

export interface OpcionesDelConceptoProps {
  principal: PlanoRenglon;
  /** Los renglones del plano tal como están (el lado actual se lee de aquí). */
  hoja: PlanoRenglon[];
  /** Las opciones guardadas de este principal. */
  opciones: PlanoOpcion[];
  formatoPrecio: (n: number) => string;
  puedeEditar: boolean;
  obra: string | null;
  plano: string | null;
  /** Búsqueda por texto en los catálogos del plano (gratis, sin IA). */
  buscarTexto: (q: string) => Promise<ConceptoParaOpcion[]>;
  /** «Recomendados» para lo que mide el renglón (el mismo motor que al elegir el concepto la primera vez). */
  recomendar: () => Promise<SugerirConceptosResponse>;
  /** Regla de oro: ¿queda bolsa? (se revisa antes de armar). */
  revisarSaldo: () => Promise<{ alcanza: boolean; aviso: string | null }>;
  /** Arma la opción (medición + complementarios) con el concepto ELEGIDO del catálogo. */
  armar: (
    pedido: { elegido: ConceptoParaOpcion; texto: string },
    avance: (t: string) => void,
  ) => Promise<{ renglones: RenglonDeOpcion[]; nombre: string; avisos: string[] }>;
  guardar: (nombre: string, renglones: RenglonDeOpcion[]) => Promise<PlanoOpcion>;
  usar: (o: PlanoOpcion) => Promise<void>;
  descartar: (o: PlanoOpcion) => Promise<void>;
  /** Reemplaza los renglones de una opción propuesta (agregarle o quitarle un adicional a mano). */
  editar: (o: PlanoOpcion, renglones: RenglonDeOpcion[]) => Promise<PlanoOpcion>;
  /** «Comparar todos los muros»: arma la misma opción para este concepto y los `extras`, las guarda como un grupo y lo abre. */
  armarGrupo: (pedido: { elegido: ConceptoParaOpcion; texto: string }, extras: PlanoRenglon[], avance: (t: string) => void) => Promise<void>;
  /** Una opción que es parte de un grupo se maneja con todo el grupo. */
  abrirGrupo: (grupoId: string) => void;
  onCerrar: () => void;
}

const fmtCant = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 3 });
const unidadBonita = (u: string | null | undefined) => (u ?? '').replace(/m2$/i, 'm²').replace(/m3$/i, 'm³');
const corto = (s: string, n = 70) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

export function OpcionesDelConcepto(p: OpcionesDelConceptoProps) {
  const [modo, setModo] = useState<'comparar' | 'nueva'>(p.opciones.length ? 'comparar' : 'nueva');
  const [selId, setSelId] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<ConceptoParaOpcion[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [elegido, setElegido] = useState<ConceptoParaOpcion | null>(null);
  const [pestana, setPestana] = useState<'buscar' | 'recomendados'>('buscar');
  const [recomendados, setRecomendados] = useState<ConceptoParaOpcion[] | null>(null);
  const [notaRecomendados, setNotaRecomendados] = useState<string | null>(null);
  const [armando, setArmando] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState<'armar' | 'usar' | 'descartar' | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [avisosArmado, setAvisosArmado] = useState<string[]>([]);
  const [trabajando, setTrabajando] = useState(false);
  const [generadorDe, setGeneradorDe] = useState<RenglonDeOpcion | null>(null);
  const [agregandoAdicional, setAgregandoAdicional] = useState(false);
  // «Comparar todos los muros»: otros conceptos de la hoja a los que se les arma la misma opción.
  const [verOtros, setVerOtros] = useState(false);
  const [extras, setExtras] = useState<Set<string>>(new Set());
  // El cuadro de búsqueda recibe el foco al abrir «Otra opción» (el diálogo lo daría a la ayuda «?»).
  const entrada = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (modo !== 'nueva') return;
    const t = setTimeout(() => entrada.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [modo]);

  // La opción que se compara: la elegida, o la última propuesta (o la última, si solo hay anteriores).
  const sel = useMemo(() => {
    const porId = p.opciones.find((o) => o.id === selId);
    if (porId) return porId;
    const props = p.opciones.filter((o) => o.estado === 'propuesta');
    return (props[props.length - 1] ?? p.opciones[p.opciones.length - 1]) || null;
  }, [p.opciones, selId]);
  useEffect(() => {
    if (p.opciones.length === 0) setModo('nueva');
  }, [p.opciones.length]);

  // Lo de hoy: todo el renglón o, si la opción es de una parte de él (se juntó con otro del mismo concepto),
  // solo esa parte.
  const lado = useMemo(
    () => ladoActualDeLaHoja(p.hoja, p.principal.id, modo === 'comparar' ? sel?.elementos : null, modo === 'comparar' ? sel?.partes : null),
    [p.hoja, p.principal.id, sel, modo],
  );
  const comp = useMemo(() => (lado && sel ? compararOpciones(lado.renglones, sel.renglones) : null), [lado, sel]);

  // Búsqueda por texto mientras se escribe (350 ms), como «Buscar otro concepto…».
  useEffect(() => {
    if (modo !== 'nueva') return;
    const q = texto.trim();
    if (q.length < 2) {
      setResultados(null);
      return;
    }
    const t = setTimeout(() => {
      setBuscando(true);
      void p
        .buscarTexto(q)
        .catch(() => [] as ConceptoParaOpcion[])
        .then((r) => {
          setResultados(r);
          setBuscando(false);
        });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto, modo]);

  // «Recomendados» se piden recién al entrar a su pestaña (consultan a la IA): abrir para buscar no consume.
  // El concepto que el renglón ya tiene no se ofrece: compararlo consigo mismo no dice nada.
  useEffect(() => {
    if (modo !== 'nueva' || pestana !== 'recomendados' || recomendados != null) return;
    let vigente = true;
    p.recomendar()
      .then((res) => {
        if (!vigente) return;
        setNotaRecomendados(res.notaFaltante ?? res.nota ?? null);
        setRecomendados(
          res.sugerencias
            .filter((x) => x.clave !== p.principal.resourceClave)
            .map((x) => ({
              resourceId: x.resourceId,
              catalogId: x.catalogId,
              clave: x.clave,
              descripcion: x.descripcion,
              unidad: x.unidad ?? null,
              precio: x.precio ?? null,
              razon: x.razonModelo,
            })),
        );
      })
      .catch(() => {
        if (!vigente) return;
        setRecomendados([]);
        setNotaRecomendados('No pude traer recomendados. Búscalo en «Buscar».');
      });
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, pestana]);

  // Los otros principales de este plano a los que se les puede armar la misma opción: no el mismo concepto ya
  // elegido ni uno que ya lo lleva. Vienen marcados los parecidos (misma unidad y misma partida) y se ajusta a mano.
  const otros = useMemo(() => {
    if (!elegido) return [];
    const conceptoElegido = claveDeConceptoOpcion({ origen: 'catalogo', catalogId: elegido.catalogId, clave: elegido.clave, descripcion: elegido.descripcion, unidad: elegido.unidad });
    return p.hoja.filter((x) => {
      if (x.rol !== 'principal' || x.id === p.principal.id || x.sourceFileId !== p.principal.sourceFileId || x.apuIaStatus === 'queued' || x.apuIaStatus === 'running') return false;
      const actual = claveDeConceptoOpcion({ origen: x.catalogId && x.resourceClave ? 'catalogo' : 'ia', catalogId: x.catalogId, clave: x.resourceClave, descripcion: x.conceptoDescripcion, unidad: x.unidad });
      return actual !== conceptoElegido;
    });
  }, [elegido, p.hoja, p.principal.id, p.principal.sourceFileId]);
  const parecidos = useMemo(
    () => otros.filter((x) => unitsAreEquivalent(x.unidad, p.principal.unidad) && (x.partida ?? '') === (p.principal.partida ?? '') && (x.generador?.uso ?? '') === (p.principal.generador?.uso ?? '')),
    [otros, p.principal.unidad, p.principal.partida, p.principal.generador?.uso],
  );
  useEffect(() => {
    // Al elegir otro concepto se vuelve a empezar: nada marcado hasta que se abra la lista.
    setVerOtros(false);
    setExtras(new Set());
  }, [elegido?.resourceId]);
  const abrirOtros = () => {
    setVerOtros(true);
    setExtras(new Set(parecidos.map((x) => x.id)));
  };
  const extrasRenglones = otros.filter((x) => extras.has(x.id));
  const enGrupo = extrasRenglones.length > 0;

  const pesos = p.formatoPrecio;
  const nombreSel = sel ? (sel.estado === 'anterior' ? `Lo que tenías antes: ${sel.nombre}` : sel.nombre) : '';
  const descPrincipal = corto(p.principal.conceptoDescripcion, 90);

  const pedirArmar = async () => {
    setAviso(null);
    setTrabajando(true);
    const s = await p.revisarSaldo().catch(() => ({ alcanza: true, aviso: null }));
    setTrabajando(false);
    if (!s.alcanza) {
      setAviso(s.aviso ?? 'Ya no te queda saldo este mes: no se puede armar la opción.');
      return;
    }
    setConfirmar('armar');
  };
  const armar = async () => {
    if (!elegido) return;
    setConfirmar(null);
    setAvisosArmado([]);
    setArmando('Empezando…');
    try {
      if (enGrupo) {
        // Una opción por concepto, juntas en un grupo: el grupo abre su propia pantalla.
        await p.armarGrupo({ elegido, texto: '' }, extrasRenglones, (t) => setArmando(t));
        return;
      }
      const r = await p.armar({ elegido, texto: '' }, (t) => setArmando(t));
      setArmando('Guardando la opción…');
      const o = await p.guardar(r.nombre, r.renglones);
      setSelId(o.id);
      setAvisosArmado(r.avisos);
      setModo('comparar');
      setTexto('');
      setElegido(null);
      setResultados(null);
    } catch (e) {
      setAviso((e as Error).message || 'No se pudo armar la opción. Intenta de nuevo.');
    } finally {
      setArmando(null);
    }
  };
  const usar = async () => {
    if (!sel) return;
    setConfirmar(null);
    setTrabajando(true);
    try {
      await p.usar(sel);
      setSelId(null);
    } catch (e) {
      setAviso((e as Error).message || 'No se pudo usar la opción. Intenta de nuevo.');
    } finally {
      setTrabajando(false);
    }
  };
  /** Agrega (o quita) un adicional a mano en la opción propuesta; el servidor vuelve a poner precios y total. */
  const editarAdicionales = async (renglones: RenglonDeOpcion[]) => {
    if (!sel) return;
    // El servidor vuelve a calcular lo de cada renglón: no se manda lo que solo sirve para pintar.
    await p.editar(sel, renglones.map(({ renglonId: _r, compartido: _c, ...r }) => ({ ...r, renglonId: null, compartido: null })));
  };
  const quitarAdicional = async (r: RenglonDeOpcion) => {
    if (!sel) return;
    setTrabajando(true);
    try {
      await editarAdicionales(sel.renglones.filter((x) => x !== r));
    } catch (e) {
      setAviso((e as Error).message || 'No se pudo quitar. Intenta de nuevo.');
    } finally {
      setTrabajando(false);
    }
  };
  const descartar = async () => {
    if (!sel) return;
    setConfirmar(null);
    setTrabajando(true);
    try {
      await p.descartar(sel);
      setSelId(null);
    } catch {
      setAviso('No se pudo descartar la opción. Intenta de nuevo.');
    } finally {
      setTrabajando(false);
    }
  };

  const imprimir = () => {
    if (!comp || !sel) return;
    const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
    const celda = (r: RenglonDeOpcion | null) =>
      r
        ? `<td>${esc(r.clave ? `${r.clave} · ` : '')}${esc(r.descripcion)}${r.compartido ? `<div class="n2">${r.rol === 'principal' ? 'Su parte de este renglón, que junta otros elementos del plano' : 'Su parte de un adicional que comparte con otros conceptos'}</div>` : ''}<div class="n2">${r.sinCantidad ? '<b>Sin cantidad: el plano no la da; escríbela.</b> ' : ''}${esc(fmtCant(r.cantidad))} ${esc(unidadBonita(r.unidad))} × ${r.precioUnitario != null ? esc(pesos(r.precioUnitario)) : 'sin precio'} = <b>${r.importe != null && r.precioUnitario != null ? esc(pesos(r.importe)) : 'se genera con IA'}</b></div></td>`
        : '<td class="no">No lleva</td>';
    const filas = comp.filas
      .map((f) => `<tr${f.cambia ? ' class="c"' : ''}>${celda(f.actual)}${celda(f.opcion)}<td class="d">${signo(f.diferencia)}${esc(pesos(Math.abs(f.diferencia)))}</td></tr>`)
      .join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Comparación · ${esc(descPrincipal)}</title><style>@page{size:letter;margin:14mm}body{font:11.5px system-ui,sans-serif;color:#111;margin:0}h1{font-size:15px;margin:0 0 2px}p{margin:0 0 8px;color:#555}.cifras{display:flex;gap:10px;margin:10px 0}.cifras div{flex:1;border:1px solid #ddd;border-radius:6px;padding:6px 8px}.cifras b{display:block;font-size:15px}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #ddd;padding:5px 6px;vertical-align:top;text-align:left}th{background:#f3f3f3}td.d{text-align:right;white-space:nowrap}.n2{font-size:10.5px;color:#555;margin-top:2px}.no{color:#888}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}tr.c td{background:#fff4c2}.pie{margin-top:10px;font-size:10.5px;color:#555}</style></head><body>` +
        `<h1>Opciones de este concepto</h1><p>${esc(p.obra ?? '')}${p.plano ? ` · ${esc(p.plano)}` : ''} · ${esc(new Date().toLocaleDateString('es-MX'))}</p>` +
        `<p><b>En tu hoja ahora:</b> ${esc(p.principal.conceptoDescripcion)}<br/><b>Si cambias a:</b> ${esc(nombreSel)}</p>` +
        `<div class="cifras"><div>En tu hoja ahora<b>${esc(pesos(comp.totalActual))}</b></div><div>Si cambias a: ${esc(corto(nombreSel, 40))}<b>${esc(pesos(comp.totalOpcion))}</b>${comp.sinPrecioOpcion ? `${comp.sinPrecioOpcion} sin precio todavía` : ''}</div><div>Diferencia<b>${signo(comp.diferencia)}${esc(pesos(Math.abs(comp.diferencia)))}</b>${esc(comp.sinPrecioOpcion || comp.sinPrecioActual ? 'Sin contar lo que no tiene precio' : textoPorcentaje(comp.diferencia, comp.porcentaje))}</div></div>` +
        `<table><thead><tr><th>En tu hoja ahora</th><th>Si cambias a: ${esc(corto(nombreSel, 50))}</th><th style="text-align:right">Diferencia</th></tr></thead><tbody>${filas}</tbody></table>` +
        `<div class="pie">En amarillo, lo que cambia. Las cantidades salen de la misma medición del plano; lo que acompaña a la opción (castillos, dalas, aplanados…) es una propuesta para revisar.${comp.sinPrecioOpcion ? ' Lo que no está en tus catálogos se genera con IA al usar la opción: no entra en el total.' : ''}</div></body></html>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  const celda = (r: RenglonDeOpcion | null, quitable = false) =>
    r ? (
      <td style={estilos.td}>
        <div style={{ lineHeight: 1.35 }}>
          {r.clave ? <b>{r.clave} · </b> : null}
          {corto(r.descripcion, 140)}
        </div>
        {r.compartido && (
          <div style={estilos.sub}>
            {r.rol === 'principal'
              ? `Su parte de este renglón, que junta otros elementos del plano (${fmtCant(r.compartido.cantidadDelRenglon)} ${unidadBonita(r.unidad)} en total)`
              : `Su parte de un adicional que comparte con otros conceptos (${fmtCant(r.compartido.cantidadDelRenglon)} ${unidadBonita(r.unidad)} en total)`}
          </div>
        )}
        {r.sinCantidad ? (
          <div style={{ ...estilos.sub, color: 'var(--bad)', fontWeight: 600 }}>
            Sin cantidad: el plano no la da. Al usar la opción entra en 0 y marcada para que la escribas en la hoja.
          </div>
        ) : (
        <div style={{ ...estilos.sub, fontVariantNumeric: 'tabular-nums' }}>
          <button type="button" style={estilos.link} title="Ver su número generador" onClick={() => setGeneradorDe(r)}>
            {fmtCant(r.cantidad)} {unidadBonita(r.unidad)}
          </button>{' '}
          × {r.precioUnitario != null ? pesos(r.precioUnitario) : 'sin precio'} ={' '}
          <b style={{ color: 'var(--text-primary)' }}>{r.importe != null && r.precioUnitario != null ? pesos(r.importe) : 'se genera con IA'}</b>
        </div>
        )}
        {r.nota && r.renglonId == null && <div style={{ ...estilos.sub, color: 'var(--warn)' }}>{corto(r.nota, 220)}</div>}
        {quitable && r.rol === 'complemento' && esAgregadoPorTi(r) && (
          <div style={estilos.sub}>
            Agregado por ti{' '}
            {puedeQuitar && (
              <button type="button" style={estilos.link} disabled={trabajando} onClick={() => void quitarAdicional(r)}>
                Quitar
              </button>
            )}
          </div>
        )}
      </td>
    ) : (
      <td style={{ ...estilos.td, color: 'var(--text-tertiary)' }}>No lleva</td>
    );

  const listaDeConceptos = (lista: ConceptoParaOpcion[]) => (
    <div role="listbox" aria-label="Conceptos" style={estilos.lista}>
      {lista.map((o) => (
        <button key={o.resourceId} type="button" role="option" aria-selected={false} style={estilos.item} onClick={() => setElegido(o)}>
          <b>{o.clave}</b> · {corto(o.descripcion, 120)}
          <span style={estilos.sub}>
            {' '}
            — {unidadBonita(o.unidad) || 's/u'} · {o.precio != null ? pesos(o.precio) : 'sin precio'}
          </span>
          {o.razon && <div style={estilos.sub}>{corto(o.razon, 200)}</div>}
        </button>
      ))}
    </div>
  );

  // Solo una propuesta suelta se edita (la de un grupo se maneja con todo el grupo; una `anterior` no se toca).
  const puedeQuitar = p.puedeEditar && sel?.estado === 'propuesta' && !sel?.grupoId;
  const esAgregadoPorTi = (r: RenglonDeOpcion) => !!r.aMano || r.generador?.regla === 'La misma medición que el concepto principal';

  const filaTabla = (f: FilaComparada, i: number) => (
    <tr key={i} style={{ background: f.cambia ? 'var(--warn-weak)' : undefined }}>
      {celda(f.actual)}
      {celda(f.opcion, true)}
      <td style={{ ...estilos.td, ...estilos.num, fontWeight: 600, color: f.diferencia < 0 ? 'var(--ok)' : f.diferencia > 0 ? 'var(--bad)' : undefined }}>
        {signo(f.diferencia)}
        {pesos(Math.abs(f.diferencia))}
      </td>
    </tr>
  );

  return (
    <>
    <Dialog open onOpenChange={(_, d) => !d.open && !armando && !trabajando && confirmar == null && generadorDe == null && !agregandoAdicional && p.onCerrar()}>
      <DialogSurface style={{ width: 1000, maxWidth: 'calc(100vw - 32px)' }}>
        <DialogBody>
          <DialogTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Opciones de este concepto <FieldHelp helpKey="planos.otra_opcion" variante="accion" />
            </span>
            <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)', marginTop: 2 }} title={p.principal.conceptoDescripcion}>
              En tu hoja ahora: {descPrincipal} · {fmtCant(p.principal.cantidad)} {unidadBonita(p.principal.unidad)}
            </div>
          </DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {aviso && <div style={estilos.aviso}>{aviso}</div>}
            {p.opciones.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ok)' }} title={p.principal.conceptoDescripcion}>
                  ✓ En tu hoja ahora: {corto(p.principal.conceptoDescripcion, 46)}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>· Para comparar (no suman a tu hoja):</span>
                {p.opciones.map((o) => {
                  const activa = modo === 'comparar' && sel?.id === o.id;
                  return (
                    <Button
                      key={o.id}
                      size="small"
                      appearance={activa ? 'primary' : 'secondary'}
                      onClick={() => {
                        setSelId(o.id);
                        setModo('comparar');
                      }}
                      title={o.nombre}
                    >
                      {o.estado === 'anterior' ? 'Lo que tenías antes: ' : 'Propuesta: '}
                      {corto(o.nombre, 38)} · {pesos(o.total)}
                    </Button>
                  );
                })}
                {p.puedeEditar && modo === 'comparar' && (
                  <Button size="small" appearance="subtle" icon={<Add16Regular />} onClick={() => setModo('nueva')}>
                    Otra opción
                  </Button>
                )}
              </div>
            )}

            {modo === 'nueva' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  ¿Con qué otro concepto de tus catálogos lo quieres comparar? Se mide igual que el renglón de hoy y se vuelve a
                  proponer lo que lo acompaña.
                </div>
                <TabList selectedValue={pestana} onTabSelect={(_, d) => setPestana(d.value as typeof pestana)} size="small">
                  <Tab value="buscar">Buscar</Tab>
                  <Tab value="recomendados">Recomendados</Tab>
                </TabList>
                {pestana === 'buscar' && (
                  <Input
                    input={{ ref: entrada }}
                    contentBefore={<Search16Regular />}
                    value={texto}
                    disabled={!!armando}
                    placeholder="Busca en tus catálogos: «muro block 15», «tablaroca»…"
                    aria-label="Buscar el concepto de la opción"
                    onChange={(_, d) => {
                      setTexto(d.value);
                      setElegido(null);
                    }}
                  />
                )}
                {elegido && (
                  <div style={estilos.elegido}>
                    Elegiste <b>{elegido.clave}</b> · {corto(elegido.descripcion, 110)} — {unidadBonita(elegido.unidad) || 's/u'} ·{' '}
                    {elegido.precio != null ? pesos(elegido.precio) : 'sin precio'}{' '}
                    <button type="button" style={estilos.link} onClick={() => setElegido(null)}>
                      Elegir otro
                    </button>
                  </div>
                )}
                {!elegido && pestana === 'buscar' && (
                  <>
                    {resultados && resultados.length > 0 && listaDeConceptos(resultados)}
                    {resultados && resultados.length === 0 && !buscando && (
                      <div style={estilos.sub}>No encontré conceptos con esas palabras. Prueba con otras o mira «Recomendados».</div>
                    )}
                    {buscando && <Spinner size="extra-tiny" label="Buscando…" labelPosition="after" />}
                  </>
                )}
                {!elegido && pestana === 'recomendados' && (
                  <>
                    {recomendados == null && <Spinner size="extra-tiny" label="Buscando lo que mejor le queda…" labelPosition="after" />}
                    {notaRecomendados && <div style={estilos.sub}>{notaRecomendados}</div>}
                    {recomendados != null && recomendados.length === 0 && !notaRecomendados && (
                      <div style={estilos.sub}>No hay recomendados. Búscalo en «Buscar».</div>
                    )}
                    {recomendados != null && recomendados.length > 0 && listaDeConceptos(recomendados)}
                  </>
                )}
                {elegido && otros.length > 0 && !armando && (
                  <div style={estilos.elegido}>
                    {!verOtros ? (
                      <>
                        ¿Quieres hacer lo mismo en otros conceptos de la hoja (por ejemplo, todos los muros)?{' '}
                        <button type="button" style={estilos.link} onClick={abrirOtros}>
                          Elegir cuáles{parecidos.length ? ` (${parecidos.length} parecidos)` : ''}
                        </button>
                      </>
                    ) : (
                      <>
                        <b>Armar también para:</b> (vienen marcados los de la misma unidad y partida; quita o agrega los que quieras)
                        <div role="group" aria-label="Otros conceptos" style={{ ...estilos.lista, maxHeight: 170, marginTop: 6 }}>
                          {otros.map((x) => (
                            <label key={x.id} style={{ ...estilos.item, display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={extras.has(x.id)}
                                onChange={(e) =>
                                  setExtras((prev) => {
                                    const n = new Set(prev);
                                    if (e.target.checked) n.add(x.id);
                                    else n.delete(x.id);
                                    return n;
                                  })
                                }
                              />
                              <span>
                                {corto(x.conceptoDescripcion, 100)}
                                <span style={estilos.sub}>
                                  {' '}
                                  — {fmtCant(x.cantidad)} {unidadBonita(x.unidad)}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                        <div style={estilos.sub}>
                          {extras.size === 0
                            ? 'Sin marcar ninguno: la opción se arma solo para este concepto.'
                            : `Se arma una opción por cada uno (${extras.size + 1} en total) y se comparan juntas.`}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {armando ? (
                  <Spinner size="small" label={armando} labelPosition="after" />
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button appearance="primary" disabled={!p.puedeEditar || trabajando || !elegido} onClick={() => void pedirArmar()}>
                      {enGrupo ? `Armar para ${extrasRenglones.length + 1} conceptos` : 'Armar la opción'}
                    </Button>
                    {p.opciones.length > 0 && (
                      <Button appearance="secondary" onClick={() => setModo('comparar')}>
                        Volver a la comparación
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {modo === 'comparar' && sel && comp && lado && (
              <>
                {avisosArmado.length > 0 && <div style={estilos.aviso}>{avisosArmado.join(' ')}</div>}
                {sel.grupoId && (
                  <div style={estilos.aviso}>
                    Esta opción es parte de «{corto(sel.grupoNombre ?? 'un grupo', 70)}» y se usa junto con las demás.{' '}
                    <button type="button" style={estilos.link} onClick={() => p.abrirGrupo(sel.grupoId!)}>
                      Abrir el grupo
                    </button>
                  </div>
                )}
                <div style={estilos.guia}>
                  <b>Tu hoja todavía no cambió.</b> Estás comparando lo que tienes (✓ en tu hoja ahora) contra{' '}
                  {sel.estado === 'anterior' ? 'lo que tenías antes' : 'esta propuesta'}. Si te conviene, pulsa{' '}
                  <b>«{sel.estado === 'anterior' ? 'Volver mi hoja a lo de antes' : 'Quedarme con esta opción'}»</b>: tu hoja cambia y lo que tienes hoy se
                  guarda para poder volver. Si no, pulsa <b>«Cerrar sin cambiar nada»</b>.
                </div>
                {lado.parcial && (
                  <div style={estilos.sub}>
                    Este renglón junta varios elementos del plano con el mismo concepto; esta opción es solo de una parte de ellos, y «En tu
                    hoja ahora» muestra solo esa parte.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  <Cifra
                    marca="✓ En tu hoja ahora"
                    colorMarca="var(--ok)"
                    detalle={descPrincipal}
                    valor={pesos(comp.totalActual)}
                    sub={comp.sinPrecioActual ? `${comp.sinPrecioActual} sin precio` : 'Es lo que suma tu presupuesto hoy'}
                  />
                  <Cifra
                    marca={sel.estado === 'anterior' ? 'Si vuelves a lo de antes' : 'Si cambias a'}
                    colorMarca="var(--accent)"
                    detalle={nombreSel}
                    valor={pesos(comp.totalOpcion)}
                    sub={[
                      'Todavía no está en tu hoja',
                      comp.sinPrecioOpcion ? `${comp.sinPrecioOpcion} sin precio todavía` : null,
                      comp.sinCantidadOpcion ? `${comp.sinCantidadOpcion} sin cantidad` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                  <Cifra
                    marca="Diferencia si cambias"
                    valor={`${signo(comp.diferencia)}${pesos(Math.abs(comp.diferencia))}`}
                    color={comp.diferencia < 0 ? 'var(--ok)' : comp.diferencia > 0 ? 'var(--bad)' : undefined}
                    sub={
                      comp.sinPrecioOpcion || comp.sinPrecioActual
                        ? `Sin contar ${comp.sinPrecioOpcion + comp.sinPrecioActual === 1 ? '1 concepto sin precio' : `${comp.sinPrecioOpcion + comp.sinPrecioActual} conceptos sin precio`}`
                        : textoPorcentaje(comp.diferencia, comp.porcentaje)
                    }
                  />
                </div>
                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-secondary)', alignItems: 'center' }}>
                  <span>
                    <span style={{ display: 'inline-block', width: 12, height: 12, background: 'var(--warn-weak)', border: '1px solid var(--warn)', borderRadius: 3, verticalAlign: -2, marginRight: 5 }} />
                    Fila en ámbar: <b>cambia</b> (otro concepto, otra cantidad o solo existe de un lado)
                  </span>
                  <span>
                    <span style={{ display: 'inline-block', width: 12, height: 12, border: '1px solid var(--border-subtle)', borderRadius: 3, verticalAlign: -2, marginRight: 5 }} />
                    Sin color: igual en las dos
                  </span>
                </div>
                <div style={{ maxHeight: '40vh', overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr>
                        <th style={estilos.th}>✓ En tu hoja ahora</th>
                        <th style={estilos.th}>{sel.estado === 'anterior' ? 'Si vuelves a lo de antes' : `Si cambias a «${corto(sel.nombre, 50)}»`}</th>
                        <th style={{ ...estilos.th, textAlign: 'right' }}>Diferencia</th>
                      </tr>
                    </thead>
                    <tbody>{comp.filas.map(filaTabla)}</tbody>
                  </table>
                </div>
                <div style={estilos.sub}>
                  Toca una cantidad para ver de dónde sale. Lo que acompaña a la opción (castillos, dalas, aplanados…) es una propuesta
                  para revisar.
                  {lado.sinParte.length > 0 &&
                    ` ${lado.sinParte.length === 1 ? 'Un adicional que comparte' : `${lado.sinParte.length} adicionales que comparte`} con otros conceptos no dice cuánto le toca a este: no entra en la comparación y se queda en tu hoja.`}
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {modo === 'comparar' && sel && comp && (
              <>
                {puedeQuitar && (
                  <Button appearance="subtle" icon={<Add16Regular />} disabled={trabajando} onClick={() => setAgregandoAdicional(true)}>
                    Agregar algo que falta
                  </Button>
                )}
                {p.puedeEditar && !sel.grupoId && (
                  <Button appearance="subtle" icon={<Delete16Regular />} disabled={trabajando} onClick={() => setConfirmar('descartar')}>
                    Borrar esta opción
                  </Button>
                )}
                <Button appearance="secondary" icon={<Print16Regular />} onClick={imprimir}>
                  Imprimir comparación
                </Button>
                {p.puedeEditar && !sel.grupoId && (
                  <Button appearance="primary" icon={trabajando ? <Spinner size="extra-tiny" /> : <ArrowSwap16Regular />} disabled={trabajando} onClick={() => setConfirmar('usar')}>
                    {sel.estado === 'anterior' ? 'Volver mi hoja a lo de antes' : 'Quedarme con esta opción'}
                  </Button>
                )}
              </>
            )}
            <Button appearance="secondary" disabled={!!armando} onClick={p.onCerrar}>
              {modo === 'comparar' && sel && comp ? 'Cerrar sin cambiar nada' : 'Cerrar'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

      {/* Confirmaciones */}
      <Dialog open={confirmar != null} onOpenChange={(_, d) => !d.open && setConfirmar(null)}>
        <DialogSurface style={{ maxWidth: 480 }}>
          <DialogBody>
            <DialogTitle>
              {confirmar === 'armar' ? 'Armar la opción' : confirmar === 'usar' ? 'Cambiar tu hoja' : 'Borrar la opción'}
            </DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5 }}>
              {confirmar === 'armar' && (
                <>
                  <div>
                    Para armarla se consulta a la IA: propone lo que acompaña a «{corto(elegido?.descripcion ?? '', 60)}» (castillos, dalas,
                    aplanados…) y lo busca en tus catálogos.{' '}
                    {enGrupo
                      ? `Se hace una vez por cada uno de los ${extrasRenglones.length + 1} conceptos: son más consultas y consumen de tu saldo del mes.`
                      : 'Son pocas consultas y consumen de tu saldo del mes.'}{' '}
                    ¿Confirmas?
                  </div>
                  <div style={estilos.sub}>Lo que tus catálogos no tengan entra sin precio, para generarlo con IA solo si usas la opción.</div>
                </>
              )}
              {confirmar === 'usar' && sel && (
                <>
                  <div>
                    Tu hoja va a quedar con <b>«{corto(nombreSel, 90)}»</b> y lo que lo acompaña, en lugar de «{descPrincipal}». Lo que tienes
                    hoy se guarda como «Lo que tenías antes» para volver cuando quieras.
                  </div>
                  {sel.sinPrecio > 0 && (
                    <div style={{ fontWeight: 600 }}>
                      {sel.sinPrecio === 1 ? '1 concepto necesita IA' : `${sel.sinPrecio} conceptos necesitan IA`}: no está en tus catálogos y entra
                      marcado para «Generar con IA», que consume de tu saldo del mes cuando lo pidas. ¿Confirmas?
                    </div>
                  )}
                </>
              )}
              {confirmar === 'descartar' && sel && <div>Se borra la opción «{corto(nombreSel, 90)}». Tu hoja no cambia.</div>}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmar(null)}>
                Cancelar
              </Button>
              <Button
                appearance="primary"
                onClick={() => void (confirmar === 'armar' ? armar() : confirmar === 'usar' ? usar() : descartar())}
              >
                {confirmar === 'armar' ? 'Sí, armarla' : confirmar === 'usar' ? 'Sí, cambiar mi hoja' : 'Borrar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {agregandoAdicional && sel && (
        <AgregarAdicional
          principal={{
            descripcion: sel.renglones.find((r) => r.rol === 'principal')?.descripcion ?? p.principal.conceptoDescripcion,
            unidad: sel.renglones.find((r) => r.rol === 'principal')?.unidad ?? null,
            cantidad: sel.renglones.find((r) => r.rol === 'principal')?.cantidad ?? 0,
            cantidadMedida: sel.renglones.find((r) => r.rol === 'principal')?.cantidadMedida ?? null,
            generador: sel.renglones.find((r) => r.rol === 'principal')?.generador ?? null,
          }}
          buscarTexto={p.buscarTexto}
          formatoPrecio={pesos}
          onAgregar={(r) => editarAdicionales([...sel.renglones, r])}
          onCerrar={() => setAgregandoAdicional(false)}
        />
      )}

      {/* El generador de una cantidad */}
      <Dialog open={generadorDe != null} onOpenChange={(_, d) => !d.open && setGeneradorDe(null)}>
        <DialogSurface style={{ width: 860, maxWidth: 'calc(100vw - 32px)' }}>
          <DialogBody>
            <DialogTitle>De dónde sale la cantidad</DialogTitle>
            <DialogContent>
              {generadorDe && (
                <DocumentoGenerador
                  renglon={{
                    ...p.principal,
                    id: generadorDe.renglonId ?? 'opcion',
                    rol: generadorDe.rol,
                    resourceClave: generadorDe.clave,
                    conceptoDescripcion: generadorDe.descripcion,
                    unidad: generadorDe.unidad,
                    cantidad: generadorDe.cantidad,
                    cantidadMedida: generadorDe.cantidadMedida ?? null,
                    precioUnitario: generadorDe.precioUnitario,
                    importe: generadorDe.importe,
                    generador: generadorDe.generador,
                  }}
                  obra={p.obra}
                  plano={p.plano}
                  croquis={null}
                  conCroquis={false}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setGeneradorDe(null)}>
                Cerrar
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

function signo(n: number): string {
  return n > 0.004 ? '+' : n < -0.004 ? '−' : '';
}

function textoPorcentaje(diferencia: number, porcentaje: number | null): string {
  if (porcentaje == null) return '';
  const p = Math.abs(porcentaje);
  const t = p < 1 && p > 0 ? p.toLocaleString('es-MX', { maximumFractionDigits: 1 }) : Math.round(p).toString();
  if (Math.abs(diferencia) < 0.005) return 'Cuestan lo mismo';
  return diferencia < 0 ? `Ahorras ${t} %` : `Cuesta ${t} % más`;
}

function Cifra({
  marca,
  colorMarca,
  detalle,
  valor,
  sub,
  color,
}: {
  marca: string;
  colorMarca?: string;
  detalle?: string;
  valor: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div style={{ background: 'var(--bg-raised)', borderRadius: 6, padding: '8px 10px', minWidth: 0, borderTop: colorMarca ? `3px solid ${colorMarca}` : undefined }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: colorMarca ?? 'var(--text-tertiary)' }}>{marca}</div>
      {detalle && (
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={detalle}>
          {detalle}
        </div>
      )}
      <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sub}</div>}
    </div>
  );
}

const estilos = {
  th: {
    position: 'sticky' as const,
    top: 0,
    background: 'var(--bg-raised)',
    textAlign: 'left' as const,
    padding: '6px 8px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-subtle)',
    zIndex: 1,
  },
  td: { padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' as const },
  num: { textAlign: 'right' as const, whiteSpace: 'nowrap' as const, fontVariantNumeric: 'tabular-nums' },
  sub: { fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 },
  link: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--accent)',
    textDecoration: 'underline',
    cursor: 'pointer',
    font: 'inherit',
  },
  guia: { fontSize: 13, lineHeight: 1.45, background: 'var(--bg-raised)', borderLeft: '3px solid var(--accent)', borderRadius: 6, padding: '8px 12px' },
  aviso: { fontSize: 13, color: 'var(--warn)', background: 'var(--warn-weak)', borderRadius: 6, padding: '6px 10px' },
  elegido: { fontSize: 12.5, background: 'var(--bg-raised)', borderRadius: 6, padding: '6px 10px' },
  lista: { maxHeight: 240, overflowY: 'auto' as const, border: '1px solid var(--border-subtle)', borderRadius: 6 },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '5px 8px',
    border: 0,
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: 12,
  },
};
